import {
  AccessorialItem,
  Commodity,
  CompanyCoreInfo,
  NormalizedLoad,
  Specifications,
} from 'types/Load';
import { Undef } from 'types/UtilityTypes';
import { TMS } from 'types/enums/Integrations';
import { denormalizeDatesForTMSForm } from 'utils/parseDatesForTMSForm';

import { MGB_DEFAULT_ITEM_ID, MGB_DEFAULT_WEIGHT_UNIT } from './constants';
import {
  normalizeMGBStopState,
  resolveMGBStopCountryForSubmit,
} from './mgbGeography';
import {
  ensureMGBRouteStops,
  ensureValidMGBCommodityLegIndices,
  isMGBDropStopType,
  isMGBPickupStopType,
  syncPickupConsigneeFromMGBStops,
} from './mgbStops';
import { MGBLineHaulUnit, MGBLoadNoteType } from './types';
import {
  commodityEffectiveWeight,
  computeMGBCustomerTotalCharge,
  computeMGBLineHaulFromPerMile,
  isEmptyMGBCommodity,
  isMGBRatePerMileMode,
  normalizeMGBPiecesUnit,
  normalizeMGBRateMode,
  normalizeMGBWeightUnit,
  parseOptionalFormNumber,
  stripEmptySystemManagedRefs,
  sumMGBCommodityWeightLbs,
  toApiCollectionMethod,
  toApiNoteType,
  toMGBGrossWeightUnit,
  toMGBPiecesUnit,
} from './utils';

/** ValueUnit wire-shape used by the BE for amount + unit pairs. */
type ValueUnit<U extends string = string> = { val: number; unit: U };

type WireCommodity = {
  description: string;
  referenceNumber: string;
  quantity: number;
  totalPieces: ValueUnit;
  grossWeight: ValueUnit;
  weightTotal: number;
  weightUnit: string;
  length?: number;
  width?: number;
  height?: number;
  dimensionsUnit?: string;
  freightClass: string;
  hazardousMaterial: boolean;
  nmfc: string;
  pickupStopNumber?: number;
  dropStopNumber?: number;
};

type WireAccessorial = {
  name: string;
  code: string;
  externalId: string;
};

type WireCustomerLineItem = {
  label: string;
  unitBasis: string;
  quantity: number;
  ratePerUnitUSD: number;
  totalChargeUSD: number;
  // Required by CostLineItem / update load cast; not used for MG EdiCode.
  note: string;
  accessorialId?: number;
  // MercuryGate EDI / service type code (e.g. LGD).
  accessorialCode?: string;
};

type WireNote = {
  note: string;
  noteType: string;
};

export type CreateLoadMGBRequest = {
  suggestionId?: number;
  quoteRequestId?: number;
  threadId?: string;
  emailId?: number;
  emailExternalId?: string;
  load: {
    mode: 'TL';
    status: '';
    poNums: string;
    operator: string;
    serviceID: number;
    tmsID: number;
    customer: { externalTMSID: string; name: string };
    billTo: { externalTMSID: string; name: string };
    pickup: {
      name: string;
      addressLine1: string;
      addressLine2: string;
      city: string;
      state: string;
      zipCode: string;
      country: string;
      contact: string;
      phone: string;
      email: string;
      refNumber: string;
      apptNote: string;
      readyTime?: string;
      readyTimeEnd?: string;
      apptStartTime?: string;
      apptEndTime?: string;
    };
    consignee: {
      name: string;
      addressLine1: string;
      addressLine2: string;
      city: string;
      state: string;
      zipCode: string;
      country: string;
      contact: string;
      phone: string;
      email: string;
      refNumber: string;
      apptNote: string;
      apptStartTime?: string;
      apptEndTime?: string;
      mustDeliver?: string;
      mustDeliverEnd?: string;
    };
    // Ordered multi-stop route (MGB). When length > 2, BE builds multi-shipment ImportWeb.
    stops?: Array<{
      stopType: string;
      stopNumber: number;
      externalTMSID?: string;
      address: {
        name: string;
        addressLine1: string;
        addressLine2: string;
        city: string;
        state: string;
        zip: string;
        country: string;
      };
      contact?: string;
      phone?: string;
      email?: string;
      refNumber?: string;
      apptNote?: string;
      readyTime?: string;
      readyEndTime?: string;
      mustDeliver?: string;
      apptStartTime?: string;
      apptEndTime?: string;
    }>;
    moreThanTwoStops?: boolean;
    specifications: {
      transportType: string;
      transportSize: string;
      planningComment: string;
      totalWeight?: ValueUnit<'lbs'>;
    };
    carrier?: {
      name: string;
      externalTMSID: string;
      mcNumber: string;
      dotNumber: string;
    };
    rateData: {
      collectionMethod: 'prepaid' | 'collect' | 'third-party';
      customerRateType: string;
      customerLineHaulCharge: ValueUnit<'USD'>;
      fscPercent: number | null;
      fscFlatRate: number | null;
      fscPerMile: number | null;
      miles: number | null;
      customerLineItems: WireCustomerLineItem[];
    };
    notes: { note: string; noteType: string }[];
    commodities: WireCommodity[];
    accessorials: WireAccessorial[];
    additionalReferences: {
      qualifier: string;
      number: string;
      weight?: number;
      pieces?: number;
      shouldSendToDriver: boolean;
    }[];
  };
  metadata: {
    isPickupDateOnly: false;
    isDropoffDateOnly: false;
    acknowledgeProdTMSOverride?: boolean;
  };
};

export type BuildCreateLoadRequestOptions = {
  serviceID: number;
  tmsID: number;
  acknowledgeProdTMSOverride?: boolean;
};

/** RFC 3339 ISO string for BE NullTime fields (plain string, not { Time, Valid }). */
export function toIsoDateTimeString(
  value: string | Date | null | undefined
): string | undefined {
  if (value == null || value === '') {
    return undefined;
  }
  const date = value instanceof Date ? value : new Date(String(value).trim());
  if (Number.isNaN(date.getTime())) {
    return undefined;
  }
  return date.toISOString();
}

export function applyMGBWallClockDatesForWire(
  values: NormalizedLoad
): NormalizedLoad {
  const stops = (values.stops ?? []).map((s) => {
    const denormalized = denormalizeDatesForTMSForm(TMS.MercurygateBoomi, {
      readyTime: s.readyTime,
      readyEndTime: s.readyEndTime,
      mustDeliver: s.mustDeliver,
      apptStartTime: s.apptStartTime,
      apptEndTime: s.apptEndTime,
    }) as {
      readyTime?: typeof s.readyTime;
      readyEndTime?: typeof s.readyEndTime;
      mustDeliver?: typeof s.mustDeliver;
      apptStartTime?: typeof s.apptStartTime;
      apptEndTime?: typeof s.apptEndTime;
    };

    return {
      ...s,
      readyTime: denormalized.readyTime ?? s.readyTime,
      readyEndTime: denormalized.readyEndTime ?? s.readyEndTime,
      mustDeliver: denormalized.mustDeliver ?? s.mustDeliver,
      apptStartTime: denormalized.apptStartTime ?? s.apptStartTime,
      apptEndTime: denormalized.apptEndTime ?? s.apptEndTime,
    };
  });

  return {
    ...values,
    stops,
    pickup: denormalizeDatesForTMSForm(
      TMS.MercurygateBoomi,
      (values.pickup ?? {}) as never
    ) as NormalizedLoad['pickup'],
    consignee: denormalizeDatesForTMSForm(
      TMS.MercurygateBoomi,
      (values.consignee ?? {}) as never
    ) as NormalizedLoad['consignee'],
  };
}

function toNumber(value: number | null | undefined): number {
  return value == null || Number.isNaN(Number(value)) ? 0 : Number(value);
}

function optionalPositiveNumber(value: unknown): number | undefined {
  const n = parseOptionalFormNumber(value);
  return n != null && n > 0 ? n : undefined;
}

function trimString(value: string | null | undefined): string {
  return value?.trim?.() ?? '';
}

function buildStopContactFields(
  stop: Pick<CompanyCoreInfo, 'contact' | 'phone' | 'email'>
) {
  return {
    contact: trimString(stop.contact),
    phone: trimString(stop.phone),
    email: trimString(stop.email),
  };
}

export function buildAccessorials(
  accessorials: AccessorialItem[] | undefined
): WireAccessorial[] {
  return (accessorials ?? []).map((a) => {
    const code = String(a.value ?? a.id ?? '');
    return {
      name: a.displayText,
      code,
      externalId: code,
    };
  });
}

/**
 * Wire notes for create/update.
 *
 * Precedence:
 * 1. Drop blank note rows (empty create UI placeholders).
 * 2. Drop any Special Instructions still left in `notes` — that field lives in
 *    Load Summary (`specifications.planningComment`) only.
 * 3. Append Special Instructions from `planningComment` when non-empty.
 */
export function buildNotes(values: NormalizedLoad): WireNote[] {
  const notes = (values.notes ?? [])
    .map((n) => ({
      note: (n.note ?? n.text ?? '').trim(),
      noteType: toApiNoteType(n.noteType ?? ''),
    }))
    .filter(
      (n) => n.note !== '' && n.noteType !== MGBLoadNoteType.SpecialInstructions
    );

  const specialInstructions = trimString(
    values.specifications?.planningComment
  );
  if (specialInstructions) {
    notes.push({
      note: specialInstructions,
      noteType: MGBLoadNoteType.SpecialInstructions,
    });
  }

  return notes;
}

/** Normalize form commodity fields before update persistence or wire mapping. */
function normalizeMGBCommodity(c: Commodity): Commodity {
  const pieces = toNumber(c.totalPieces?.val);
  const weight = commodityEffectiveWeight(c);
  const weightUnit = normalizeMGBWeightUnit(
    String(c.weightUnit ?? c.grossWeight?.unit ?? MGB_DEFAULT_WEIGHT_UNIT)
  );
  const referenceNumber =
    String(c.referenceNumber ?? '').trim() || MGB_DEFAULT_ITEM_ID;

  return {
    ...c,
    description: c.description,
    referenceNumber,
    quantity: Math.trunc(pieces),
    totalPieces: {
      val: pieces,
      unit: toMGBPiecesUnit(c.totalPieces?.unit),
    },
    grossWeight: {
      val: weight,
      unit: toMGBGrossWeightUnit(weightUnit),
    },
    weightTotal: weight,
    weightUnit,
    length: optionalPositiveNumber(c.length) as unknown as number,
    width: optionalPositiveNumber(c.width) as unknown as number,
    height: optionalPositiveNumber(c.height) as unknown as number,
    dimensionUnit: trimString(c.dimensionUnit) || 'in',
    freightClass: trimString(c.freightClass),
    hazardousMaterial: Boolean(c.hazardousMaterial),
    nmfc: c.nmfc?.trim() ?? '',
  };
}

/**
 * Map a normalized commodity to create-load wire shape.
 * Omits length/width/height/dimensionsUnit unless at least one dimension is > 0.
 */
function commodityToWire(c: Commodity): WireCommodity {
  const length = optionalPositiveNumber(c.length);
  const width = optionalPositiveNumber(c.width);
  const height = optionalPositiveNumber(c.height);
  const hasDimensions = length != null || width != null || height != null;

  return {
    description: c.description,
    referenceNumber: c.referenceNumber,
    quantity: c.quantity,
    totalPieces: {
      val: c.totalPieces?.val ?? 0,
      unit: normalizeMGBPiecesUnit(c.totalPieces?.unit),
    },
    grossWeight: {
      val: c.grossWeight?.val ?? 0,
      unit: normalizeMGBWeightUnit(String(c.grossWeight?.unit ?? '')),
    },
    weightTotal: c.weightTotal,
    weightUnit: normalizeMGBWeightUnit(c.weightUnit),
    ...(length != null ? { length } : {}),
    ...(width != null ? { width } : {}),
    ...(height != null ? { height } : {}),
    ...(hasDimensions
      ? { dimensionsUnit: trimString(c.dimensionUnit) || 'in' }
      : {}),
    freightClass: c.freightClass,
    hazardousMaterial: c.hazardousMaterial,
    nmfc: c.nmfc,
    ...(c.pickupStopNumber != null
      ? { pickupStopNumber: Number(c.pickupStopNumber) }
      : {}),
    ...(c.dropStopNumber != null
      ? { dropStopNumber: Number(c.dropStopNumber) }
      : {}),
  };
}

/** Normalized commodities for update-load (`Load.commodities`). */
export function buildLoadCommodities(
  values: NormalizedLoad['commodities']
): Commodity[] {
  return (values ?? [])
    .filter((c) => !isEmptyMGBCommodity(c))
    .map((c) => normalizeMGBCommodity(c));
}

/** Create-load wire commodities (skips empty placeholder rows). */
export function buildCommodities(
  values: NormalizedLoad['commodities']
): WireCommodity[] {
  return buildLoadCommodities(values).map(commodityToWire);
}

export function buildAdditionalReferences(
  values: NormalizedLoad
): CreateLoadMGBRequest['load']['additionalReferences'] {
  return stripEmptySystemManagedRefs(values.additionalReferences)
    .filter(
      (ref) => trimString(ref.qualifier) !== '' && trimString(ref.number) !== ''
    )
    .map((ref) => {
      const weight = optionalPositiveNumber(ref.weight);
      const pieces = optionalPositiveNumber(ref.pieces);
      return {
        qualifier: trimString(ref.qualifier),
        number: trimString(ref.number),
        ...(weight != null ? { weight } : {}),
        ...(pieces != null ? { pieces } : {}),
        shouldSendToDriver: Boolean(ref.shouldSendToDriver),
      };
    });
}

export const COLLECTION_METHOD_REQUIRED_ERROR = 'paymentMethod is required';

/** Map form payment method to ImportWeb wire values; throws if unset/unknown. */
export function wireCollectionMethod(
  values: NormalizedLoad
): 'prepaid' | 'collect' | 'third-party' {
  const api = toApiCollectionMethod(values.rateData?.collectionMethod ?? '');
  if (api === 'prepaid' || api === 'collect' || api === 'third-party') {
    return api;
  }
  throw new Error(COLLECTION_METHOD_REQUIRED_ERROR);
}

export function buildRateData(values: NormalizedLoad) {
  const fscPercent = parseOptionalFormNumber(values.rateData?.fscPercent);
  const fscFlatRate = parseOptionalFormNumber(values.rateData?.fscFlatRate);
  const fscPerMile = parseOptionalFormNumber(values.rateData?.fscPerMile);
  const miles = parseOptionalFormNumber(values.rateData?.miles);
  const customerLineItems = (values.rateData?.customerLineItems ?? [])
    .filter((item) => toNumber(item.totalChargeUSD) > 0 && item.label?.trim())
    .map((item) => {
      const wire: WireCustomerLineItem = {
        label: item.label.trim(),
        unitBasis: item.unitBasis || 'flat',
        quantity: toNumber(item.quantity) || 1,
        ratePerUnitUSD: toNumber(item.ratePerUnitUSD),
        totalChargeUSD: toNumber(item.totalChargeUSD),
        note: item.note ?? '',
      };
      if (item.accessorialId != null && item.accessorialId > 0) {
        wire.accessorialId = item.accessorialId;
      }
      const code = item.accessorialCode?.trim();
      if (code) {
        wire.accessorialCode = code;
      }
      return wire;
    });

  const ratePerMileMode = isMGBRatePerMileMode(
    values.rateData?.customerLineHaulUnit
  );
  const ratePerMile = parseOptionalFormNumber(
    values.rateData?.customerLineHaulRate
  );
  const flatLineHaulVal = toNumber(
    values.rateData?.customerLineHaulCharge?.val
  );

  // PM: charge = miles × $/mi; wire unit + numUnits for MG price sheet (ENG-7727).
  let lineHaulVal = flatLineHaulVal;
  let customerLineHaulRate = flatLineHaulVal;
  let customerLineHaulUnit: string = MGBLineHaulUnit.Flat;
  let customerRateNumUnits = 1;

  if (ratePerMileMode) {
    const computed = computeMGBLineHaulFromPerMile(miles, ratePerMile);
    lineHaulVal = computed ?? 0;
    customerLineHaulRate = ratePerMile ?? 0;
    customerLineHaulUnit = MGBLineHaulUnit.PerMile;
    customerRateNumUnits = miles != null && miles > 0 ? miles : 0;
  }

  const customerLineHaulCharge = {
    val: lineHaulVal,
    unit: 'USD' as const,
  };

  return {
    collectionMethod: wireCollectionMethod(values),
    customerRateType: normalizeMGBRateMode(values.rateData?.customerRateType),
    customerLineHaulCharge,
    customerLineHaulRate,
    customerLineHaulUnit,
    customerRateNumUnits,
    fscPercent,
    fscFlatRate,
    fscPerMile,
    miles,
    customerLineItems,
    customerTotalCharge: {
      val: computeMGBCustomerTotalCharge({
        customerLineHaulCharge,
        fscPercent,
        fscFlatRate,
        fscPerMile,
        miles,
        customerLineItems,
      }),
      unit: 'USD' as const,
    },
  };
}

export function buildSpecifications(values: NormalizedLoad) {
  // Recompute from line items so mixed kg/lb is converted, not naively summed as lbs.
  // MG ImportWeb uses per-item weights; this field is Drumkit persistence + UI.
  const totalWeightVal = sumMGBCommodityWeightLbs(values.commodities);
  const totalWeight =
    totalWeightVal > 0
      ? { val: totalWeightVal, unit: 'lbs' as const }
      : undefined;

  return {
    transportType: values.specifications.transportType,
    transportSize: values.specifications.transportSize,
    planningComment: values.specifications.planningComment,
    ...(totalWeight ? { totalWeight } : {}),
  };
}

/**
 * Update payload: preserve specifications from the loaded record and form,
 * while applying MG-Boomi wire fields from {@link buildSpecifications}.
 */
export function mergeMGBSpecificationsForUpdate(
  values: NormalizedLoad,
  existing?: Specifications
): Specifications {
  return {
    ...existing,
    ...values.specifications,
    ...buildSpecifications(values),
  } as Specifications;
}

/**
 * Mirror multi-stop form state into classic pickup/consignee + ensure stop numbers
 * before create/update payload mapping.
 */
export function prepareMGBValuesForWire(
  values: NormalizedLoad
): NormalizedLoad {
  const stops = ensureMGBRouteStops(values).map((s, i) => ({
    ...s,
    stopNumber: i,
  }));
  const synced = syncPickupConsigneeFromMGBStops({
    ...values,
    stops,
  });
  const commodities = (values.commodities ?? []).map((c) => {
    if (isEmptyMGBCommodity(c)) {
      return c;
    }
    const leg = ensureValidMGBCommodityLegIndices(
      stops,
      c.pickupStopNumber,
      c.dropStopNumber
    );
    return {
      ...c,
      pickupStopNumber: leg.pickup,
      dropStopNumber: leg.drop,
    };
  });

  return {
    ...values,
    stops,
    pickup: synced.pickup,
    consignee: synced.consignee,
    moreThanTwoStops: synced.moreThanTwoStops,
    commodities,
  };
}

function wireStopType(stopType: Undef<string>): string {
  if (isMGBPickupStopType(stopType)) {
    return 'pickup';
  }
  if (isMGBDropStopType(stopType)) {
    return 'dropoff';
  }

  return String(stopType || 'dropoff');
}

export function buildWireStops(
  values: NormalizedLoad
): NonNullable<CreateLoadMGBRequest['load']['stops']> {
  const stops = values.stops ?? [];
  return stops.map((s, index) => {
    const isPickup = isMGBPickupStopType(s.stopType);
    const readyTime = toIsoDateTimeString(
      isPickup ? s.readyTime : s.mustDeliver
    );
    const readyEndTime = toIsoDateTimeString(s.readyEndTime);
    const apptStart = toIsoDateTimeString(s.apptStartTime);
    const apptEnd = toIsoDateTimeString(s.apptEndTime);
    const state = String(s.address?.state ?? '').trim();
    const country = resolveMGBStopCountryForSubmit(s.address?.country, state);
    const stopType = wireStopType(s.stopType);
    // Pickups carry readyTime; drops send the same instant as mustDeliver.
    const readyTimeField = isPickup
      ? { readyTime }
      : { mustDeliver: readyTime };

    return {
      stopType,
      stopNumber: index,
      externalTMSID: s.externalTMSID ?? '',
      address: {
        name: s.address?.name ?? '',
        addressLine1: s.address?.addressLine1 ?? '',
        addressLine2: s.address?.addressLine2 ?? '',
        city: s.address?.city ?? '',
        state,
        zip: s.address?.zip ?? '',
        country,
      },
      contact: s.contact ?? '',
      phone: s.phone ?? '',
      email: s.email ?? '',
      refNumber: s.refNumber ?? '',
      apptNote: s.apptNote ?? '',
      ...(readyTime ? readyTimeField : {}),
      ...(readyEndTime ? { readyEndTime } : {}),
      ...(apptStart ? { apptStartTime: apptStart } : {}),
      ...(apptEnd ? { apptEndTime: apptEnd } : {}),
    };
  });
}

/**
 * Build the exact `POST /load` payload from normalized form state.
 * Only conversion layer — form uses `NormalizedLoad` like other TMSes.
 */
export function buildCreateLoadRequest(
  values: NormalizedLoad,
  meta: BuildCreateLoadRequestOptions
): CreateLoadMGBRequest {
  const prepared = applyMGBWallClockDatesForWire(
    prepareMGBValuesForWire(values)
  );
  const pickupReady = toIsoDateTimeString(prepared.pickup?.readyTime);
  const pickupReadyEnd = toIsoDateTimeString(prepared.pickup?.readyTimeEnd);
  const pickupStart = toIsoDateTimeString(prepared.pickup?.apptStartTime);
  const pickupEnd = toIsoDateTimeString(prepared.pickup?.apptEndTime);
  const mustDeliver = toIsoDateTimeString(prepared.consignee?.mustDeliver);
  const mustDeliverEnd = toIsoDateTimeString(
    prepared.consignee?.mustDeliverEnd
  );
  const consigneeStart = toIsoDateTimeString(prepared.consignee?.apptStartTime);
  const consigneeEnd = toIsoDateTimeString(prepared.consignee?.apptEndTime);
  const wireStops = buildWireStops(prepared);

  return {
    load: {
      mode: 'TL',
      status: '',
      poNums: prepared.poNums?.trim?.() ?? '',
      operator: prepared.operator?.trim?.() ?? '',
      serviceID: meta.serviceID,
      tmsID: meta.tmsID,
      customer: {
        externalTMSID: prepared.customer.externalTMSID,
        name: prepared.customer.name,
      },
      billTo: {
        externalTMSID:
          prepared.billTo.externalTMSID?.trim?.() ||
          prepared.customer.externalTMSID,
        name: prepared.billTo.name?.trim?.() || prepared.customer.name,
      },
      pickup: {
        name: prepared.pickup.name,
        addressLine1: prepared.pickup.addressLine1,
        addressLine2: prepared.pickup.addressLine2,
        city: prepared.pickup.city,
        state: normalizeMGBStopState(prepared.pickup.state ?? ''),
        zipCode: prepared.pickup.zipCode,
        country: resolveMGBStopCountryForSubmit(
          prepared.pickup.country,
          prepared.pickup.state
        ),
        ...buildStopContactFields(prepared.pickup),
        refNumber: prepared.pickup.refNumber,
        apptNote: prepared.pickup.apptNote,
        ...(pickupReady ? { readyTime: pickupReady } : {}),
        ...(pickupReadyEnd ? { readyTimeEnd: pickupReadyEnd } : {}),
        ...(pickupStart ? { apptStartTime: pickupStart } : {}),
        ...(pickupEnd ? { apptEndTime: pickupEnd } : {}),
      },
      consignee: {
        name: prepared.consignee.name,
        addressLine1: prepared.consignee.addressLine1,
        addressLine2: prepared.consignee.addressLine2,
        city: prepared.consignee.city,
        state: normalizeMGBStopState(prepared.consignee.state ?? ''),
        zipCode: prepared.consignee.zipCode,
        country: resolveMGBStopCountryForSubmit(
          prepared.consignee.country,
          prepared.consignee.state
        ),
        ...buildStopContactFields(prepared.consignee),
        refNumber: prepared.consignee.refNumber,
        apptNote: prepared.consignee.apptNote,
        ...(mustDeliver ? { mustDeliver } : {}),
        ...(mustDeliverEnd ? { mustDeliverEnd } : {}),
        ...(consigneeStart ? { apptStartTime: consigneeStart } : {}),
        ...(consigneeEnd ? { apptEndTime: consigneeEnd } : {}),
      },
      stops: wireStops,
      moreThanTwoStops: wireStops.length > 2,
      specifications: buildSpecifications(prepared),
      rateData: buildRateData(prepared),
      notes: buildNotes(prepared),
      commodities: buildCommodities(prepared.commodities),
      accessorials: buildAccessorials(prepared.accessorials),
      additionalReferences: buildAdditionalReferences(prepared),
    },
    metadata: {
      isPickupDateOnly: false,
      isDropoffDateOnly: false,
      ...(meta.acknowledgeProdTMSOverride
        ? { acknowledgeProdTMSOverride: true }
        : {}),
    },
  };
}
